import React from "react";
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaArrowLeft, FaShieldAlt, FaUserCheck, FaServer, FaFileContract, FaGavel, FaExclamationTriangle, FaRegCopyright } from 'react-icons/fa';
import { analytics } from "../features/firebase/firebase-config";
import SEO from "../features/seo/seo.feature";

const TermsAndConditions: React.FC = () => {
  analytics.logEvent('page_view', {
    page_title: 'terms and conditions',
  });

  // Define structured data for better SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Terms and Conditions | PrepBestie",
    "description": "Terms and conditions for using PrepBestie's AI-powered interview and speech practice platform",
    "publisher": {
      "@type": "Organization",
      "name": "PrepBestie",
      "logo": {
        "@type": "ImageObject",
        "url": "https://prepbestie.com/prepbestie.png"
      }
    },
    "inLanguage": "en",
    "lastReviewed": new Date().toISOString().split('T')[0],
    "mainContentOfPage": {
      "@type": "WebPageElement",
      "cssSelector": ".terms-content"
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <SEO
        title='Terms and Conditions | PrepBestie'
        description='Terms and conditions for using PrepBestie platform - our user agreement covering account registration, services, content policies, intellectual property, and legal terms.'
        name='PrepBestie'
        type='website'
        structuredData={structuredData}
        keywords={[
          'Prep Bestie',
          'PrepBestie',
          'Terms and Conditions',
          'Legal',
          'Terms of Use',
          'Interviews',
          'Interview preparation',
          'Pitch preparation',
          'Public speech',
          'AI',
          'AI tools',
          'User agreement',
          'Service terms',
          'Account registration',
          'Content policy',
          'Privacy terms'
        ]}
      />

      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-bold text-gray-900">Terms and Conditions</h1>
            <Link
              to="/"
              className="flex items-center text-indigo-600 hover:text-indigo-800 transition"
            >
              <FaArrowLeft className="mr-2" />
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <motion.div
          className="bg-white shadow-md rounded-xl p-8 mb-8 terms-content"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-2xl font-semibold text-gray-800 mb-6">Terms and Conditions for PrepBestie</h2>
          <p className="text-gray-600 mb-4"><strong>Last Updated:</strong> {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>

          <section className="mb-8">
            <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <FaFileContract className="text-indigo-600 mr-2" />
              1. Introduction
            </h3>
            <p className="text-gray-600 mb-4">
              Welcome to PrepBestie ("us", "we", "our", or the "Company"). These Terms and Conditions (the "Terms") govern your access to and use of the PrepBestie website, mobile applications, and services (collectively, the "Services") offered by PrepBestie.
            </p>
            <p className="text-gray-600 mb-4">
              By accessing or using our Services, you ("User", "you", or "your") agree to be bound by these Terms. If you do not agree with any part of these Terms, you must not access or use our Services.
            </p>
            <p className="text-gray-600 mb-4">
              Please read these Terms carefully before using our Services. These Terms constitute a legally binding agreement between you and PrepBestie.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <FaUserCheck className="text-indigo-600 mr-2" />
              2. Eligibility and Account Registration
            </h3>
            <h4 className="text-lg font-medium text-gray-800 mb-2">2.1 Age Requirement</h4>
            <p className="text-gray-600 mb-4">
              You must be at least 18 years old, or the age of majority in your jurisdiction, whichever is greater, to access or use our Services. By using our Services, you represent and warrant that you meet this age requirement.
            </p>

            <h4 className="text-lg font-medium text-gray-800 mb-2">2.2 Account Registration</h4>
            <p className="text-gray-600 mb-4">
              To access certain features of our Services, you may be required to register for an account. You agree to provide accurate, current, and complete information during the registration process and to update such information to keep it accurate, current, and complete.
            </p>

            <h4 className="text-lg font-medium text-gray-800 mb-2">2.3 Account Security</h4>
            <p className="text-gray-600 mb-4">
              You are responsible for maintaining the confidentiality of your account credentials, including your password, and for all activities that occur under your account. You agree to notify us immediately of any unauthorized use of your account or any other breach of security.
            </p>

            <h4 className="text-lg font-medium text-gray-800 mb-2">2.4 Account Termination</h4>
            <p className="text-gray-600 mb-4">
              We reserve the right to suspend or terminate your account and your access to the Services at any time, for any reason, without prior notice or liability. You may also terminate your account at any time by following the instructions on our website or by contacting us.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <FaServer className="text-indigo-600 mr-2" />
              3. Services Description
            </h3>
            <h4 className="text-lg font-medium text-gray-800 mb-2">3.1 AI-Powered Features</h4>
            <p className="text-gray-600 mb-4">
              PrepBestie provides AI-powered services that help users practice for interviews, pitches, presentations, and public speaking engagements. Our Services may include, but are not limited to, AI-generated practice questions, real-time feedback, performance analysis, and personalized improvement recommendations.
            </p>

            <h4 className="text-lg font-medium text-gray-800 mb-2">3.2 Service Modifications</h4>
            <p className="text-gray-600 mb-4">
              We reserve the right to modify, suspend, or discontinue any part of our Services at any time, with or without notice. We will not be liable to you or any third party for any modification, suspension, or discontinuation of the Services.
            </p>

            <h4 className="text-lg font-medium text-gray-800 mb-2">3.3 Service Limitations</h4>
            <p className="text-gray-600 mb-4">
              The Services are provided on an "as is" and "as available" basis. We do not guarantee that the Services will be uninterrupted, secure, or error-free. We are not responsible for any delays, delivery failures, or other damages resulting from such problems.
            </p>

            <h4 className="text-lg font-medium text-gray-800 mb-2">3.4 No Professional Advice</h4>
            <p className="text-gray-600 mb-4">
              Our Services provide general information and AI-generated feedback for practice purposes only. The Services do not provide professional advice or guarantees of success in actual interviews or speaking engagements. You should use your own judgment and seek professional advice when appropriate.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <FaFileContract className="text-indigo-600 mr-2" />
              4. User Content
            </h3>
            <h4 className="text-lg font-medium text-gray-800 mb-2">4.1 User Content Definition</h4>
            <p className="text-gray-600 mb-4">
              "User Content" refers to any content you submit, upload, or otherwise make available through the Services, including text responses, audio recordings, video recordings, practice scenarios, and feedback.
            </p>

            <h4 className="text-lg font-medium text-gray-800 mb-2">4.2 Ownership of User Content</h4>
            <p className="text-gray-600 mb-4">
              You retain ownership of your User Content. However, by submitting User Content to the Services, you grant us a worldwide, non-exclusive, royalty-free, sublicensable, and transferable license to use, reproduce, distribute, prepare derivative works of, display, and perform your User Content in connection with providing and improving the Services.
            </p>

            <h4 className="text-lg font-medium text-gray-800 mb-2">4.3 AI Training</h4>
            <p className="text-gray-600 mb-4">
              With your consent, we may use your anonymized User Content to train and improve our AI models. You can withdraw your consent for this use at any time through your account settings or by contacting us.
            </p>

            <h4 className="text-lg font-medium text-gray-800 mb-2">4.4 User Content Restrictions</h4>
            <p className="text-gray-600 mb-4">
              You agree not to submit User Content that:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li>Violates any applicable law or regulation</li>
              <li>Infringes the intellectual property rights of others</li>
              <li>Contains malicious code, viruses, or harmful components</li>
              <li>Is defamatory, obscene, harassing, threatening, or abusive</li>
              <li>Promotes discrimination, bigotry, racism, or harm against any individual or group</li>
              <li>Contains personal data of third parties without their consent</li>
              <li>Is otherwise inappropriate or harmful</li>
            </ul>

            <h4 className="text-lg font-medium text-gray-800 mb-2">4.5 Content Removal</h4>
            <p className="text-gray-600 mb-4">
              We reserve the right to remove any User Content that violates these Terms or that we deem objectionable in our sole discretion, without notice or liability.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <FaRegCopyright className="text-indigo-600 mr-2" />
              5. Intellectual Property Rights
            </h3>
            <h4 className="text-lg font-medium text-gray-800 mb-2">5.1 Our Intellectual Property</h4>
            <p className="text-gray-600 mb-4">
              The Services, including all content, features, functionality, software, interfaces, text, graphics, logos, button icons, images, audio clips, videos, data compilations, and the design, selection, and arrangement thereof (excluding User Content), are owned by PrepBestie, our licensors, or other providers of such material and are protected by copyright, trademark, patent, trade secret, and other intellectual property or proprietary rights laws.
            </p>

            <h4 className="text-lg font-medium text-gray-800 mb-2">5.2 Limited License</h4>
            <p className="text-gray-600 mb-4">
              Subject to your compliance with these Terms, we grant you a limited, non-exclusive, non-transferable, revocable license to access and use the Services for your personal, non-commercial use. This license does not include any resale or commercial use of the Services or its contents.
            </p>

            <h4 className="text-lg font-medium text-gray-800 mb-2">5.3 Restrictions</h4>
            <p className="text-gray-600 mb-4">
              You may not:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li>Reproduce, distribute, publicly display, or publicly perform the Services or any part thereof</li>
              <li>Modify, create derivative works of, reverse engineer, decompile, or disassemble the Services or any part thereof</li>
              <li>Remove any copyright, trademark, or other proprietary notices from the Services</li>
              <li>Use any data mining, robots, or similar data gathering or extraction methods on the Services</li>
              <li>Use the Services in any manner that could disable, overburden, damage, or impair the Services</li>
              <li>Access the Services through automated means or for any purpose other than as intended</li>
              <li>Use the Services for any illegal or unauthorized purpose</li>
            </ul>
          </section>

          <section className="mb-8">
            <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <FaShieldAlt className="text-indigo-600 mr-2" />
              6. Data Privacy and Security
            </h3>
            <h4 className="text-lg font-medium text-gray-800 mb-2">6.1 Privacy Policy</h4>
            <p className="text-gray-600 mb-4">
              Our collection, use, and disclosure of your personal information is governed by our <Link to="/privacy" className="text-indigo-600 hover:text-indigo-800 transition">Privacy Policy</Link>, which is incorporated into these Terms by reference. By using our Services, you consent to the processing of your personal information as described in our Privacy Policy.
            </p>

            <h4 className="text-lg font-medium text-gray-800 mb-2">6.2 Cookie Policy</h4>
            <p className="text-gray-600 mb-4">
              Our use of cookies and similar technologies is governed by our <Link to="/cookies" className="text-indigo-600 hover:text-indigo-800 transition">Cookie Policy</Link>. By using our Services, you consent to our use of cookies as described in our Cookie Policy.
            </p>

            <h4 className="text-lg font-medium text-gray-800 mb-2">6.3 Data Processing</h4>
            <p className="text-gray-600 mb-4">
              Our processing of your personal data is further explained in our <Link to="/data-processing" className="text-indigo-600 hover:text-indigo-800 transition">Data Processing Policy</Link>. This includes information about how we process your data, legal bases for processing, international transfers, and your rights regarding your data.
            </p>

            <h4 className="text-lg font-medium text-gray-800 mb-2">6.4 Security</h4>
            <p className="text-gray-600 mb-4">
              We implement appropriate technical and organizational measures to protect your personal data against unauthorized or unlawful processing, accidental loss, destruction, or damage. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <FaExclamationTriangle className="text-indigo-600 mr-2" />
              7. Limitation of Liability
            </h3>
            <h4 className="text-lg font-medium text-gray-800 mb-2">7.1 Disclaimer of Warranties</h4>
            <p className="text-gray-600 mb-4">
              THE SERVICES ARE PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS, WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. TO THE FULLEST EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>

            <h4 className="text-lg font-medium text-gray-800 mb-2">7.2 Limitation of Liability</h4>
            <p className="text-gray-600 mb-4">
              TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL PREPBESTIE, ITS AFFILIATES, DIRECTORS, EMPLOYEES, AGENTS, OR LICENSORS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO, DAMAGES FOR LOSS OF PROFITS, GOODWILL, USE, DATA, OR OTHER INTANGIBLE LOSSES, RESULTING FROM:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li>YOUR ACCESS TO OR USE OF OR INABILITY TO ACCESS OR USE THE SERVICES</li>
              <li>ANY CONDUCT OR CONTENT OF ANY THIRD PARTY ON THE SERVICES</li>
              <li>ANY CONTENT OBTAINED FROM THE SERVICES</li>
              <li>UNAUTHORIZED ACCESS, USE, OR ALTERATION OF YOUR TRANSMISSIONS OR CONTENT</li>
            </ul>
            <p className="text-gray-600 mb-4">
              IN NO EVENT SHALL OUR TOTAL LIABILITY TO YOU FOR ALL CLAIMS EXCEED THE AMOUNT PAID BY YOU, IF ANY, FOR ACCESSING OR USING OUR SERVICES DURING THE TWELVE (12) MONTHS PRECEDING THE EVENT GIVING RISE TO THE LIABILITY.
            </p>

            <h4 className="text-lg font-medium text-gray-800 mb-2">7.3 Exclusions</h4>
            <p className="text-gray-600 mb-4">
              Some jurisdictions do not allow the exclusion of certain warranties or the limitation or exclusion of liability for incidental or consequential damages. Accordingly, some of the above limitations may not apply to you.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <FaShieldAlt className="text-indigo-600 mr-2" />
              8. Indemnification
            </h3>
            <p className="text-gray-600 mb-4">
              You agree to defend, indemnify, and hold harmless PrepBestie, its affiliates, licensors, and service providers, and its and their respective officers, directors, employees, contractors, agents, licensors, suppliers, successors, and assigns from and against any claims, liabilities, damages, judgments, awards, losses, costs, expenses, or fees (including reasonable attorneys' fees) arising out of or relating to:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li>Your violation of these Terms</li>
              <li>Your User Content</li>
              <li>Your use of the Services</li>
              <li>Your violation of any third-party right, including without limitation any intellectual property right, privacy right, or publicity right</li>
              <li>Any claim that your User Content caused damage to a third party</li>
            </ul>
          </section>

          <section className="mb-8">
            <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <FaGavel className="text-indigo-600 mr-2" />
              9. Dispute Resolution
            </h3>
            <h4 className="text-lg font-medium text-gray-800 mb-2">9.1 Governing Law</h4>
            <p className="text-gray-600 mb-4">
              These Terms and your use of the Services shall be governed by and construed in accordance with the laws of the jurisdiction in which PrepBestie operates, without giving effect to any principles of conflicts of law.
            </p>

            <h4 className="text-lg font-medium text-gray-800 mb-2">9.2 Informal Dispute Resolution</h4>
            <p className="text-gray-600 mb-4">
              Before filing a claim against PrepBestie, you agree to try to resolve the dispute informally by contacting us. We'll try to resolve the dispute informally by contacting you via email. If a dispute is not resolved within 30 days of submission, you or PrepBestie may proceed to formal dispute resolution.
            </p>

            <h4 className="text-lg font-medium text-gray-800 mb-2">9.3 Binding Arbitration</h4>
            <p className="text-gray-600 mb-4">
              Any dispute, controversy, or claim arising out of or relating to these Terms or the Services that cannot be resolved informally shall be resolved by binding arbitration on an individual basis in accordance with the rules of the American Arbitration Association. Any judgment on the award rendered by the arbitrator may be entered in any court of competent jurisdiction.
            </p>

            <h4 className="text-lg font-medium text-gray-800 mb-2">9.4 Class Action Waiver</h4>
            <p className="text-gray-600 mb-4">
              YOU AND PREPBESTIE AGREE THAT EACH MAY BRING CLAIMS AGAINST THE OTHER ONLY IN YOUR OR ITS INDIVIDUAL CAPACITY, AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS OR REPRESENTATIVE PROCEEDING.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <FaFileContract className="text-indigo-600 mr-2" />
              10. Termination
            </h3>
            <p className="text-gray-600 mb-4">
              We may terminate or suspend your access to the Services immediately, without prior notice or liability, for any reason whatsoever, including without limitation if you breach these Terms. Upon termination, your right to use the Services will immediately cease.
            </p>
            <p className="text-gray-600 mb-4">
              All provisions of these Terms which by their nature should survive termination shall survive termination, including, without limitation, ownership provisions, warranty disclaimers, indemnity, and limitations of liability.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <FaFileContract className="text-indigo-600 mr-2" />
              11. Changes to Terms
            </h3>
            <p className="text-gray-600 mb-4">
              We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is material, we will provide at least 30 days' notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion.
            </p>
            <p className="text-gray-600 mb-4">
              By continuing to access or use our Services after those revisions become effective, you agree to be bound by the revised Terms. If you do not agree to the new Terms, you must stop using the Services.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <FaFileContract className="text-indigo-600 mr-2" />
              12. Miscellaneous
            </h3>
            <h4 className="text-lg font-medium text-gray-800 mb-2">12.1 Entire Agreement</h4>
            <p className="text-gray-600 mb-4">
              These Terms, together with our Privacy Policy, Cookie Policy, and Data Processing Policy, constitute the entire agreement between you and PrepBestie regarding the Services and supersede all prior and contemporaneous agreements, proposals, or representations, written or oral, concerning the Services.
            </p>

            <h4 className="text-lg font-medium text-gray-800 mb-2">12.2 Waiver</h4>
            <p className="text-gray-600 mb-4">
              No waiver of any term of these Terms shall be deemed a further or continuing waiver of such term or any other term, and our failure to assert any right or provision under these Terms shall not constitute a waiver of such right or provision.
            </p>

            <h4 className="text-lg font-medium text-gray-800 mb-2">12.3 Severability</h4>
            <p className="text-gray-600 mb-4">
              If any provision of these Terms is held by a court or other tribunal of competent jurisdiction to be invalid, illegal, or unenforceable for any reason, such provision shall be eliminated or limited to the minimum extent such that the remaining provisions of the Terms will continue in full force and effect.
            </p>

            <h4 className="text-lg font-medium text-gray-800 mb-2">12.4 Assignment</h4>
            <p className="text-gray-600 mb-4">
              You may not assign or transfer these Terms, by operation of law or otherwise, without our prior written consent. Any attempt by you to assign or transfer these Terms without such consent will be null and of no effect. We may assign or transfer these Terms, at our sole discretion, without restriction.
            </p>

            <h4 className="text-lg font-medium text-gray-800 mb-2">12.5 Notices</h4>
            <p className="text-gray-600 mb-4">
              Any notices or other communications provided by PrepBestie under these Terms will be given by posting to the Services or by email to the email address you provide to us. Notices provided by posting to the Services will be effective upon posting, and notices provided by email will be effective when sent.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <FaFileContract className="text-indigo-600 mr-2" />
              13. Contact Information
            </h3>
            <p className="text-gray-600 mb-4">
              If you have any questions or concerns about these Terms, please contact us at:
            </p>
            <p className="text-gray-600 mb-4">
              <a href="mailto:legal@prepbestie.com" className="text-indigo-600 hover:text-indigo-800 transition">legal@prepbestie.com</a>
            </p>
            <p className="text-gray-600 mb-4 italic">
              By using PrepBestie's Services, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.
            </p>
          </section>
        </motion.div>
      </main>

      <footer className="bg-gray-100 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-gray-500 text-sm">
          &copy; {new Date().getFullYear()} PrepBestie. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default TermsAndConditions;
